import type { SupplyAgentLogisticsCalculateResponse } from '@/services/supplyAgentService';

/** Qué parte de la carga se muestra: todo, un proveedor o una clasificación ABC. */
export type LoadEstimateFilter =
  | { kind: 'all' }
  | { kind: 'supplier'; supplierId: number }
  | { kind: 'abc'; value: string };

export interface OccupancyGroup {
  key: string;
  label: string;
  /** Ocupación del contenedor: la mayor entre volumen, peso y posiciones de pallet, que es la que limita. */
  pct: number | null;
  volumePct: number | null;
  weightPct: number | null;
  palletPct: number | null;
  filter: LoadEstimateFilter;
}

export interface OccupancyBreakdown {
  total: OccupancyGroup;
  bySupplier: OccupancyGroup[];
  byAbc: OccupancyGroup[];
}

type Line = Record<string, unknown>;

const num = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const lineUnits = (line: Line) => num(line.suggested_units ?? line.suggested_quantity);
const lineVolume = (line: Line) => lineUnits(line) * num(line.effective_unit_volume_m3);
const lineWeight = (line: Line) => lineUnits(line) * num(line.effective_unit_weight_kg);
const linePallets = (line: Line) => {
  const perPallet = num(line.units_per_pallet);
  return perPallet > 0 ? lineUnits(line) / perPallet : 0;
};

const estimateLines = (estimate: SupplyAgentLogisticsCalculateResponse): Line[] => estimate.before?.lines_detail ?? [];

const abcOf = (line: Line, abcByProduct: Record<string, string>) => abcByProduct[String(line.product_id)] || '—';

export function matchesLoadFilter(line: Line, filter: LoadEstimateFilter, abcByProduct: Record<string, string>): boolean {
  if (filter.kind === 'supplier') return num(line.supplier_id) === filter.supplierId;
  if (filter.kind === 'abc') return abcOf(line, abcByProduct) === filter.value;
  return true;
}

/** Posiciones de suelo con pallets de 1,2 × 0,8 m, igual que la vista del camión; sin ellas en LCL o a granel. */
function capacity(estimate: SupplyAgentLogisticsCalculateResponse) {
  const container = estimate.container || {};
  const byPallet = estimate.load_mode !== 'bulk' && estimate.shipment_mode !== 'lcl';
  const positions = byPallet
    ? Math.floor(num(container.internal_length_mm) / 1200) * Math.floor(num(container.internal_width_mm) / 800)
    : 0;
  return { volume: num(container.usable_volume_m3), weight: num(container.usable_payload_kg), positions };
}

function group(
  key: string,
  label: string,
  lines: Line[],
  cap: { volume: number; weight: number; positions: number },
  filter: LoadEstimateFilter,
  roundPallets = false,
): OccupancyGroup {
  const volume = lines.reduce((total, line) => total + lineVolume(line), 0);
  const weight = lines.reduce((total, line) => total + lineWeight(line), 0);
  const volumePct = cap.volume > 0 ? (volume / cap.volume) * 100 : null;
  const weightPct = cap.weight > 0 && weight > 0 ? (weight / cap.weight) * 100 : null;
  const pallets = lines.reduce((total, line) => total + linePallets(line), 0);
  const palletPct = cap.positions > 0 && pallets > 0
    ? ((roundPallets ? Math.ceil(pallets - 1e-9) : pallets) / cap.positions) * 100
    : null;
  const candidates = [volumePct, weightPct, palletPct].filter((value): value is number => value != null);
  return { key, label, pct: candidates.length > 0 ? Math.max(...candidates) : null, volumePct, weightPct, palletPct, filter };
}

/** Ocupación total y su reparto por proveedor y por clasificación ABC (cada tramo suma al total). */
export function buildOccupancyBreakdown(
  estimate: SupplyAgentLogisticsCalculateResponse,
  abcByProduct: Record<string, string>,
  totalLabel: string,
): OccupancyBreakdown {
  const lines = estimateLines(estimate);
  const cap = capacity(estimate);

  const suppliers = new Map<number, { name: string; lines: Line[] }>();
  lines.forEach((line) => {
    const id = num(line.supplier_id);
    const entry = suppliers.get(id) ?? { name: String(line.supplier_name || id), lines: [] };
    entry.lines.push(line);
    suppliers.set(id, entry);
  });

  const abcs = new Map<string, Line[]>();
  lines.forEach((line) => {
    const value = abcOf(line, abcByProduct);
    abcs.set(value, [...(abcs.get(value) ?? []), line]);
  });

  return {
    total: group('all', totalLabel, lines, cap, { kind: 'all' }, true),
    bySupplier: [...suppliers.entries()]
      .map(([id, entry]) => group(`s${id}`, entry.name, entry.lines, cap, { kind: 'supplier', supplierId: id }))
      .sort((a, b) => (b.pct ?? 0) - (a.pct ?? 0)),
    byAbc: [...abcs.entries()]
      .map(([value, entryLines]) => group(`a${value}`, value, entryLines, cap, { kind: 'abc', value }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  };
}

/** Totales de unas líneas (unidades, pallets, volumen y peso) tal como los espera la estimación. */
export function snapshotOf(lines: Line[]) {
  return {
    units: lines.reduce((total, line) => total + lineUnits(line), 0),
    pallet_equivalent: lines.reduce((total, line) => total + linePallets(line), 0),
    volume_m3: lines.reduce((total, line) => total + lineVolume(line), 0),
    weight_kg: lines.reduce((total, line) => total + lineWeight(line), 0),
    lines_detail: lines,
  };
}

/** Estimación con solo las líneas del filtro y los totales recalculados, para que camión y cifras coincidan. */
export function filterLoadEstimate(
  estimate: SupplyAgentLogisticsCalculateResponse,
  filter: LoadEstimateFilter,
  abcByProduct: Record<string, string>,
): SupplyAgentLogisticsCalculateResponse {
  if (filter.kind === 'all') return estimate;
  const lines = estimateLines(estimate).filter((line) => matchesLoadFilter(line, filter, abcByProduct));
  const cap = capacity(estimate);
  const snapshot = snapshotOf(lines);
  const volume = snapshot.volume_m3;
  const weight = snapshot.weight_kg;
  return {
    ...estimate,
    before: snapshot,
    after: snapshot,
    lines_detail: lines,
    utilization: {
      ...estimate.utilization,
      volume_m3_before: volume,
      volume_m3_after: volume,
      weight_kg_before: weight,
      weight_kg_after: weight,
      volume_fill_pct_after: cap.volume > 0 ? (volume / cap.volume) * 100 : undefined,
      weight_fill_pct_after: cap.weight > 0 ? (weight / cap.weight) * 100 : undefined,
    },
  };
}
